import { CircleNotch } from "@phosphor-icons/react";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames/bind";
import React, { useMemo, useRef } from "react";
import { Checkbox } from "../../../Checkbox";
import styles from "../DropdownInput.module.scss";
import { useDropdownContext } from "../DropdownContext";
import DropdownOption from "./DropdownOption";

const cx = classNames.bind(styles);

const DropdownMenuContent = () => {
  const {
    value,
    onChange,
    isMulti,
    isMultiValue,
    getValue,
    getLabel,
    isBusy,
    pagination,
    setPagination,
    data,
    isMenuOpen,
    isSortedAlphabetically,
    onFetchData,
    onScroll,
    hasGroups,
  } = useDropdownContext();

  const springProps = useSpring({
    loop: true,
    delay: 0,
    from: {
      transform: "rotate(0deg)",
    },
    to: { transform: "rotate(360deg)" },
    config: {
      duration: 750,
    },
  });

  const isAtBottomRange = useRef<boolean>(false);

  const AnimatedCircleNotch = animated(CircleNotch);

  const initialDisplayOptions = useMemo(() => {
    if (!isMulti) {
      return (
        (isSortedAlphabetically && !onFetchData
          ? data?.options?.toSorted((optionA: any, optionB: any) => {
              const labelA = getLabel?.(optionA) ?? "";
              const labelB = getLabel?.(optionB) ?? "";

              return labelA > labelB ? 1 : -1;
            })
          : data?.options) ?? []
      );
    }

    return onFetchData
      ? data?.options
      : data
        ? [
            ...(data.options?.sort((optionA: any, optionB: any) => {
              const valueA = getValue?.(optionA) ?? "";
              const labelA = getLabel?.(optionA) ?? "";
              const valueB = getValue?.(optionB) ?? "";
              const labelB = getLabel?.(optionB) ?? "";

              const checkValue = Array.isArray(value) ? value : [value];

              // If both items are selected or both items are not selected, sort alphabetically
              if (
                isSortedAlphabetically &&
                ((!checkValue?.includes(valueA) &&
                  !checkValue?.includes(valueB)) ||
                  (checkValue?.includes(valueA) &&
                    checkValue?.includes(valueB)))
              ) {
                return labelA > labelB ? 1 : -1;
              }

              // Otherwise, move the selected options to the top
              if (
                checkValue?.includes(valueA) &&
                !checkValue?.includes(valueB)
              ) {
                return -1;
              }

              if (
                !checkValue?.includes(valueA) &&
                checkValue?.includes(valueB)
              ) {
                return 1;
              }

              return 0;
            }) ?? []),
          ]
        : [];
  }, [data?.options, isMulti, isMenuOpen]);

  const filteredOptions = useMemo(() => {
    if (!onFetchData) {
      return initialDisplayOptions.filter((option: any) =>
        getLabel?.(option)
          ?.toLowerCase()
          .includes(pagination?.searchTerm?.toLowerCase() ?? "")
      );
    }

    return data?.options ?? [];
  }, [initialDisplayOptions, pagination?.searchTerm, data?.options]);

  const handleIncludeAll = () => {
    if (!isMulti || !isMultiValue(hasGroups, value)) {
      return;
    }

    if (filteredOptions.length) {
      const newSelectedOpts =
        value.length < filteredOptions.length
          ? filteredOptions.map((option: any) => getValue?.(option))
          : [];
      onChange?.(newSelectedOpts);
    } else {
      const newSelectedOpts =
        value.length < data.options.length
          ? data?.options.map((option: any) => getValue?.(option))
          : [];

      onChange?.(newSelectedOpts);
    }
  };

  const onMenuScroll = (event: any) => {
    // Re: CHB-3735: For some users, the isNearBottom doesn't get triggered correctly.
    // Adding an arbitrary 14px safe margin (half the height of dropdown item) will help with this issue:
    const bottomDistanceSafeMargin = 14;

    const isNearBottom =
      event.target.scrollHeight -
        event.target.scrollTop -
        bottomDistanceSafeMargin <=
      event.target.clientHeight;

    // Pass onScroll event along:
    onScroll?.({ isBottom: isNearBottom }, event);

    // These checks prevent extra setPagination calls if user scrolls within the safe margin:
    if (isNearBottom && !isAtBottomRange.current) {
      isAtBottomRange.current = true;
      setPagination?.({ page: (pagination?.page ?? 1) + 1 });
    }

    if (!isNearBottom && isAtBottomRange.current) {
      isAtBottomRange.current = false;
    }
  };

  return (
    <ul
      className={cx("list")}
      onScroll={onMenuScroll}
    >
      {isMulti && filteredOptions?.length ? (
        <>
          <div
            className={cx("option")}
            role="option"
            tabIndex={0}
            onClick={handleIncludeAll}
          >
            <Checkbox
              name="dropdown-include-all-checkbox"
              label="Include All"
              value={
                filteredOptions.length
                  ? filteredOptions.every((option: any) =>
                      value?.includes(getValue?.(option))
                    )
                  : data?.options.length
                    ? data?.options.every((option: any) =>
                        value?.includes(getValue?.(option))
                      )
                    : false
              }
              isFocusable={false}
            />
          </div>

          <hr className={cx("separator")} />
        </>
      ) : null}
      {filteredOptions?.length ? (
        filteredOptions.map((option: any, idx: number) => (
          <DropdownOption
            key={`${getValue?.(option)}-${idx}`}
            option={option}
          />
        ))
      ) : (
        <li className={cx("emptyText")}>No options found</li>
      )}

      {isBusy ? (
        <div className={cx("loadingIndicator")}>
          <AnimatedCircleNotch
            size={24}
            style={springProps}
            className={cx("loader")}
          />
        </div>
      ) : null}
    </ul>
  );
};

export default DropdownMenuContent;
